import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { stripHtml } from "../../../utils/avatarUtils";
import "./HoverPreview.css";

/** Space between anchor and popover (tooltip-style, below text). */
const PREVIEW_GAP_PX = 4;
const VIEW_MARGIN_PX = 8;

const HoverPreview = ({
  content = "",
  isHtml = false,
  /** When `content` is HTML, pass plain text so the cell preview truncates correctly. */
  plainTextForPreview = null,
  previewLength = 40,
  /** When `false`, long text is cut without appending `...` (e.g. Title / Description columns). */
  appendEllipsis = true,
  maxWidth = 360,
  maxHeight = 240,
  className = "",
  /** Shown when there is no text (default em dash). Pass `""` for a visually empty cell. */
  emptyPlaceholder = "—",
  /** When `isHtml`, max lines shown in the cell before clipping (full HTML stays in hover popover). */
  htmlAnchorLineClamp = 3,
  /**
   * When `isHtml` is true: if false, the anchor shows truncated plain text (one line + ellipsis)
   * while the popover still renders full HTML. Use for tiles/cards where Quill markup breaks line-clamp.
   */
  anchorShowHtml = true,
  /**
   * When set with `isHtml`: anchor renders this HTML string; popover still uses `content`.
   * Use for compact badges / layouts where anchor and popover markup differ.
   */
  anchorHtml = null,
  /** When true, hover popover is always available (e.g. audit log notes). */
  forcePreview = false,
}) => {
  const anchorRef = useRef(null);
  const popoverRef = useRef(null);
  const closeTimerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });
  /** True when the cell clips text (CSS ellipsis in narrow table columns). */
  const [hasVisualOverflow, setHasVisualOverflow] = useState(false);

  const textContent = useMemo(() => {
    if (content == null) return "";
    if (typeof content === "string") return content;
    return String(content);
  }, [content]);

  const previewBase = useMemo(() => {
    if (plainTextForPreview != null) return String(plainTextForPreview);
    return textContent;
  }, [plainTextForPreview, textContent]);

  /** Plain text for truncation / overflow logic (HTML cells pass stripped text). */
  const plainForLogic = useMemo(() => {
    if (plainTextForPreview != null) return String(plainTextForPreview).trim();
    if (isHtml) return stripHtml(textContent).trim();
    return previewBase.trim();
  }, [plainTextForPreview, isHtml, textContent, previewBase]);

  const useCustomAnchorHtml = Boolean(
    isHtml &&
      anchorHtml != null &&
      String(anchorHtml).trim().length > 0,
  );

  const useRichAnchor = Boolean(
    !useCustomAnchorHtml &&
      anchorShowHtml &&
      isHtml &&
      plainForLogic.length > 0,
  );

  const previewText = useMemo(() => {
    if (isHtml && plainForLogic.length === 0) return emptyPlaceholder;
    const trimmed = previewBase.trim();
    if (trimmed.length === 0) return emptyPlaceholder;
    if (trimmed.length <= previewLength) return trimmed;
    const cut = trimmed.slice(0, previewLength);
    return appendEllipsis ? `${cut}...` : cut;
  }, [
    isHtml,
    plainForLogic.length,
    previewBase,
    previewLength,
    appendEllipsis,
    emptyPlaceholder,
  ]);

  const logicalTruncated = plainForLogic.length > previewLength;

  const canShowPopover =
    forcePreview ||
    logicalTruncated ||
    hasVisualOverflow ||
    Boolean(isHtml && !anchorShowHtml && plainForLogic.length > 0) ||
    useCustomAnchorHtml;

  /** Narrow table cells use CSS ellipsis — open preview whenever text is clipped (shows …). */
  useLayoutEffect(() => {
    const el = anchorRef.current;
    if (!el) return undefined;

    const measure = () => {
      requestAnimationFrame(() => {
        const node = anchorRef.current;
        if (!node) return;
        if (useRichAnchor) {
          const hOverflow = node.scrollHeight > node.clientHeight + 1;
          const wOverflow = node.scrollWidth > node.clientWidth + 1;
          setHasVisualOverflow(hOverflow || wOverflow);
        } else {
          setHasVisualOverflow(node.scrollWidth > node.clientWidth + 1);
        }
      });
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [
    previewText,
    previewBase,
    className,
    useRichAnchor,
    useCustomAnchorHtml,
    textContent,
    htmlAnchorLineClamp,
  ]);

  useEffect(() => {
    if (!pinned) return undefined;
    const onDocMouseDown = (e) => {
      const t = e.target;
      if (anchorRef.current?.contains(t) || popoverRef.current?.contains(t)) {
        return;
      }
      setPinned(false);
      setIsOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [pinned]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setPinned(false);
        setIsOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen]);

  /** Place popover just below the anchor (left-aligned), flip above if needed — matches table tooltip UX. */
  useLayoutEffect(() => {
    if (!isOpen) return undefined;
    const anchor = anchorRef.current;
    const pop = popoverRef.current;
    if (!anchor || !pop) return undefined;

    const rect = anchor.getBoundingClientRect();
    const pr = pop.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let left = rect.left;
    if (left + pr.width > vw - VIEW_MARGIN_PX) {
      left = Math.max(VIEW_MARGIN_PX, vw - VIEW_MARGIN_PX - pr.width);
    }
    if (left < VIEW_MARGIN_PX) left = VIEW_MARGIN_PX;

    let top = rect.bottom + PREVIEW_GAP_PX;
    if (top + pr.height > vh - VIEW_MARGIN_PX) {
      top = rect.top - PREVIEW_GAP_PX - pr.height;
    }
    if (top < VIEW_MARGIN_PX) top = VIEW_MARGIN_PX;

    setPopoverPos((prev) =>
      prev.top === top && prev.left === left ? prev : { top, left },
    );
    return undefined;
  }, [isOpen, textContent, maxWidth, maxHeight, isHtml, anchorHtml]);

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const openPopover = () => {
    clearCloseTimer();
    if (!canShowPopover) return;
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;

    setPopoverPos({
      top: rect.bottom + PREVIEW_GAP_PX,
      left: rect.left,
    });
    setIsOpen(true);
  };

  const closePopoverWithDelay = () => {
    if (pinned) return;
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setIsOpen(false);
      closeTimerRef.current = null;
    }, 180);
  };

  const handleAnchorClick = (e) => {
    if (!canShowPopover) return;
    e.stopPropagation();
    if (pinned) {
      setPinned(false);
      setIsOpen(false);
      return;
    }
    setPinned(true);
    openPopover();
  };

  const anchorClassName = [
    className,
    useRichAnchor ? "hover-preview__anchor-rich" : "",
    useCustomAnchorHtml ? "hover-preview__anchor-custom-html" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const anchorStyle = useRichAnchor
    ? {
        cursor: canShowPopover ? "pointer" : "default",
        display: "-webkit-box",
        WebkitBoxOrient: "vertical",
        WebkitLineClamp: htmlAnchorLineClamp,
        width: "100%",
        minWidth: 0,
        maxWidth: "100%",
        overflow: "hidden",
        wordBreak: "break-word",
        whiteSpace: "normal",
        verticalAlign: "bottom",
      }
    : {
        cursor: canShowPopover ? "pointer" : "default",
        display: "block",
        width: "100%",
        minWidth: 0,
        maxWidth: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        verticalAlign: "bottom",
      };

  const titlePlain = plainForLogic || previewBase.trim() || stripHtml(textContent).trim();

  return (
    <>
      <span
        ref={anchorRef}
        className={anchorClassName}
        style={anchorStyle}
        title={!canShowPopover ? titlePlain || undefined : undefined}
        onMouseEnter={openPopover}
        onMouseLeave={closePopoverWithDelay}
        onClick={handleAnchorClick}
        role={canShowPopover ? "button" : undefined}
        tabIndex={canShowPopover ? 0 : undefined}
        onKeyDown={(e) => {
          if (!canShowPopover || e.repeat) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleAnchorClick(e);
          }
        }}
      >
        {useRichAnchor ? (
          <div className="ql-snow">
            <div
              className="ql-editor"
              dangerouslySetInnerHTML={{ __html: textContent }}
            />
          </div>
        ) : useCustomAnchorHtml ? (
          <span
            className="hover-preview__custom-anchor-inner"
            dangerouslySetInnerHTML={{ __html: String(anchorHtml) }}
          />
        ) : (
          previewText
        )}
      </span>
      {isOpen &&
        createPortal(
          <div
            ref={popoverRef}
            onMouseEnter={openPopover}
            onMouseLeave={closePopoverWithDelay}
            style={{
              position: "fixed",
              top: popoverPos.top,
              left: popoverPos.left,
              zIndex: 2000,
              width: maxWidth,
              maxHeight,
              overflow: "auto",
              background: "#fff",
              border: "1px solid rgba(0,0,0,0.15)",
              borderRadius: "0.5rem",
              boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
              padding: "0.75rem",
              color: "#334155",
              whiteSpace: "normal",
              fontSize: "0.85rem",
              lineHeight: 1.4,
            }}
          >
            {isHtml ? (
              <div className="ql-snow">
                <div
                  className="ql-editor"
                  dangerouslySetInnerHTML={{ __html: textContent }}
                />
              </div>
            ) : (
              <div>{textContent}</div>
            )}
          </div>,
          document.body,
        )}
    </>
  );
};

export default HoverPreview;
